// ==== STAGE3「ショピダンジョン」階層バトルエンジン ====
// rpg-quiz-loader.js が公開する window.RPGQuiz（fetchDungeonMonsters）で
// dungeon_monster metaobject（名前・画像・出題リスト）を取得し、
// コマンド選択（たたかう/おたすけ/にげる）を挟むターン制バトルとして描画・進行管理する。
class RPGDungeon {
  constructor(root) {
    this.root = root;
    this.sectionId = root.dataset.sectionId;
    this.storageKey = `rpg_dungeon_floor_${this.sectionId}`;
    this.playerMaxHp = parseInt(root.dataset.playerHp, 10) || 5;
    this.clearMessage = root.dataset.clearMessage || '';
    this.clearButtonLabel = root.dataset.clearButtonLabel || '';
    this.clearButtonLink = root.dataset.clearButtonLink || '';
    this.timeLimitSeconds = parseInt(root.dataset.timeLimit, 10) || 20;
    this.heroName = this.escapeHtml(root.dataset.heroName || '勇者');

    this.bindElements(root);

    this.bgmEnabled = true;
    this.timerTimeoutId = null;
    this.questionActive = false;
    this.wrongQuestions = []; // 間違えた（時間切れ含む）問題を、後でクリア画面から振り返れるように記録する

    // 「おたすけ」で助けてくれるメンバー一覧。今はさかもっちゃんのみだが、
    // 将来メンバーを増やす時はここに追加するだけで選択肢に出るようにしてある
    // helper.applyEffect(data, assignment) が実際の効果（選択肢を減らす・ヒントを見せる等）を担当するので、
    // メンバーごとに効果を変えたい場合はここに新しいhelperを追加してapplyEffectを実装するだけでよい
    this.helpers = [
      {
        id: 'sakamocchan',
        name: 'さかもっちゃん',
        helpMessage: 'さかもっちゃんはショピ魔法で問題を弱体化した！',
        // ショピ魔法：不正解の選択肢を1つランダムに消して正解しやすくする
        applyEffect: (data, assignment) => {
          const wrongChoices = data.answers
            .map((answer, index) => ({ answer, index }))
            .filter((c) => c.index !== data.correct);
          if (wrongChoices.length > 0) {
            const remove = wrongChoices[Math.floor(Math.random() * wrongChoices.length)];
            assignment.removedChoiceIndex = remove.index;
          }
        },
      },
    ];
    // 顔アイコンのURLはliquid側のJSON（asset_url）から取得し、idで突き合わせて各helperに付与する
    const helperIcons = this.parseHelperIcons(root);
    this.helpers.forEach((helper) => {
      helper.icon = helperIcons[helper.id] || '';
    });

    this.totalFloors = 1;
    this.bossFloor = 1;
    this.monsterPool = [];
    this.bossMonster = null;
    this.backgrounds = this.parseBackgrounds(root);
    this.floorCache = {}; // 一度訪れた階のモンスター/背景/出題を同一セッション内で固定する

    this.bindCommands();
    this.bindKeyboardNav();
    this.bindBgmToggle();
    this.bindStartScreen();
    this.bindReviewModal();
    this.dataReadyPromise = this.prepareData();
  }

  // data-role属性からDOM要素をまとめて取得する（役割ごとにグループ分けしてある）
  bindElements(root) {
    const byRole = (role) => root.querySelector(`[data-role="${role}"]`);

    // バトル画面
    this.floorLabelEl = byRole('rpg-dungeon-floor-label');
    this.heartsEl = byRole('rpg-dungeon-hearts');
    this.monsterNameEl = byRole('rpg-dungeon-monster-name');
    this.stageEl = byRole('rpg-dungeon-stage');
    this.statusEl = byRole('rpg-dungeon-status');
    this.quizPanelEl = byRole('rpg-dungeon-quiz-panel');
    this.questionEl = byRole('rpg-dungeon-question');
    this.answersEl = byRole('rpg-dungeon-answers');
    this.feedbackEl = byRole('rpg-dungeon-feedback');
    this.commandsEl = byRole('rpg-dungeon-commands');
    this.commandLabelEl = byRole('rpg-dungeon-command-label');

    // BGM・効果音
    this.bgmEl = byRole('rpg-dungeon-bgm');
    this.bgmToggleEl = byRole('rpg-dungeon-bgm-toggle');
    this.attackSfxEl = byRole('rpg-dungeon-attack-sfx');
    this.stairsSfxEl = byRole('rpg-dungeon-stairs-sfx');
    this.helpSfxEl = byRole('rpg-dungeon-help-sfx');
    this.victorySfxEl = byRole('rpg-dungeon-victory-sfx');
    this.clearSfxEl = byRole('rpg-dungeon-clear-sfx');

    // ホーム画面
    this.startScreenEl = byRole('rpg-dungeon-start-screen');
    this.startBtnEl = byRole('rpg-dungeon-start-btn');
    this.continueBtnEl = byRole('rpg-dungeon-continue-btn');
    this.gameEl = byRole('rpg-dungeon-game');

    // 制限時間バー
    this.timerEl = byRole('rpg-dungeon-timer');
    this.timerBarEl = byRole('rpg-dungeon-timer-bar');

    // 間違えた問題の解説モーダル
    this.reviewModalEl = byRole('rpg-dungeon-review-modal');
    this.reviewListEl = byRole('rpg-dungeon-review-list');
    this.reviewCloseEl = byRole('rpg-dungeon-review-close');
  }

  // ---- ホーム画面 ----
  // 保存済みの進行状況があれば「続きから始める」ボタンも並べて出す。
  // メインのボタンは常に1階からのリセット開始で、セーブがある時だけ文言を「1階からはじめる」に変える
  bindStartScreen() {
    if (!this.startBtnEl) return;
    if (this.hasSavedProgress()) {
      this.startBtnEl.textContent = '1階からはじめる';
      if (this.continueBtnEl) this.continueBtnEl.hidden = false;
    }

    this.startBtnEl.addEventListener('click', () => {
      this.root.classList.add('is-started');
      this.tryPlayBgm();
      this.startFromFirstFloor();
    });

    if (this.continueBtnEl) {
      this.continueBtnEl.addEventListener('click', () => {
        this.root.classList.add('is-started');
        this.tryPlayBgm();
        this.startGame();
      });
    }
  }

  // 逃げてリセットされた直後（1階のまま）は「続きから」と呼べるほどの進行がないので、
  // 2階以降まで進んでいる場合だけ「続きから始める」を出す
  hasSavedProgress() {
    try {
      const floor = parseInt(localStorage.getItem(this.storageKey), 10);
      return Number.isInteger(floor) && floor > 1;
    } catch (err) {
      return false;
    }
  }

  // ---- 解説モーダル ----
  bindReviewModal() {
    if (!this.reviewModalEl) return;
    if (this.reviewCloseEl) {
      this.reviewCloseEl.addEventListener('click', () => this.closeReviewModal());
    }
    this.reviewModalEl.addEventListener('click', (event) => {
      if (event.target === this.reviewModalEl) this.closeReviewModal();
    });
    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && !this.reviewModalEl.hidden) this.closeReviewModal();
    });
  }

  // 間違えた（時間切れ含む）問題を、クリア画面から見返せるようにモーダルへ描画する
  openReviewModal() {
    if (!this.reviewModalEl || !this.reviewListEl) return;
    this.reviewListEl.innerHTML = '';

    this.wrongQuestions.forEach(({ monsterName, question, correctAnswer, explanation, explanationImage }) => {
      const item = document.createElement('div');
      item.className = 'rpg-dungeon-review-item';

      const monster = document.createElement('div');
      monster.className = 'rpg-dungeon-review-item__monster';
      monster.textContent = monsterName;
      item.appendChild(monster);

      const questionEl = document.createElement('div');
      questionEl.className = 'rpg-dungeon-review-item__question';
      questionEl.innerHTML = question;
      item.appendChild(questionEl);

      const correctAnswerEl = document.createElement('div');
      correctAnswerEl.className = 'rpg-dungeon-review-item__correct';
      correctAnswerEl.innerHTML = `<strong>正解：</strong>${correctAnswer}`;
      item.appendChild(correctAnswerEl);

      const explanationEl = document.createElement('div');
      explanationEl.className = 'rpg-dungeon-review-item__explanation';
      explanationEl.innerHTML = explanation || '解説はありません';
      item.appendChild(explanationEl);

      if (explanationImage) {
        const img = document.createElement('img');
        img.className = 'rpg-dungeon-review-item__image';
        img.src = explanationImage;
        img.alt = '';
        item.appendChild(img);
      }

      this.reviewListEl.appendChild(item);
    });

    this.reviewModalEl.hidden = false;
    // .rpg-dungeon-section 自体がスタッキングコンテキストを作っているため、
    // position:fixed のモーダルでもヘッダーより手前に出すにはセクション側のz-indexを一時的に上げる必要がある
    this.root.classList.add('rpg-dungeon-modal-open');
  }

  closeReviewModal() {
    if (!this.reviewModalEl) return;
    this.reviewModalEl.hidden = true;
    this.root.classList.remove('rpg-dungeon-modal-open');
  }

  // ---- BGM・効果音 ----
  // 「ゲームスタート」ボタン押下という明確なユーザー操作をきっかけにBGMを再生する
  // （ブラウザの自動再生制限を、ユーザー操作なしの再生試行では回避できないため）
  tryPlayBgm() {
    if (!this.bgmEl || !this.bgmEnabled) return;
    this.bgmEl.currentTime = 0;
    this.bgmEl.play().catch(() => {});
  }

  bindBgmToggle() {
    if (this.bgmEl) this.bgmEl.volume = 0.4;
    if (!this.bgmToggleEl) return;
    this.bgmToggleEl.addEventListener('click', () => {
      this.bgmEnabled = !this.bgmEnabled;
      this.bgmToggleEl.textContent = this.bgmEnabled ? '🔊' : '🔇';
      this.bgmToggleEl.setAttribute('aria-pressed', String(this.bgmEnabled));
      if (this.bgmEnabled) this.tryPlayBgm();
      else this.bgmEl.pause();
    });
  }

  // 効果音再生の共通処理。ミュート中（bgmEnabled=false）は効果音も鳴らさない
  playSfx(audioEl) {
    if (!audioEl || !this.bgmEnabled) return;
    audioEl.currentTime = 0;
    audioEl.play().catch(() => {});
  }

  playAttackSfx() {
    this.playSfx(this.attackSfxEl);
  }

  playStairsSfx() {
    this.playSfx(this.stairsSfxEl);
  }

  playHelpSfx() {
    this.playSfx(this.helpSfxEl);
  }

  playVictorySfx() {
    this.playSfx(this.victorySfxEl);
  }

  playClearSfx() {
    this.playSfx(this.clearSfxEl);
  }

  // モンスターがダメージを受けたことを見せるヒットアニメーション（連続ヒットにも対応するため一度クラスを外してから再付与する）
  playHitAnimation() {
    if (!this.monsterImgEl) return;
    this.monsterImgEl.classList.remove('is-hit');
    void this.monsterImgEl.offsetWidth;
    this.monsterImgEl.classList.add('is-hit');
  }

  // ---- ゲーム開始・進行状況 ----
  // ホーム画面表示中にあらかじめ取得しておき、「ゲームスタート」を押した時点で
  // 待たせずに1階を表示できるようにする。
  // 通常モンスターは1体ずつ登場（重複なし）、全員倒したら最終階でBOSSが登場する構成。
  // 階数はdungeon_monsterメタオブジェクトの登録数に応じて自動で決まる
  async prepareData() {
    const monsters = await this.fetchMonsters();
    this.monsterPool = this.shuffle(monsters.filter((m) => !m.isBoss));
    this.bossMonster = monsters.find((m) => m.isBoss) || null;
    this.totalFloors = this.monsterPool.length + 1;
    this.bossFloor = this.totalFloors;
  }

  async startGame() {
    await this.dataReadyPromise;

    if (this.monsterPool.length === 0 || !this.bossMonster) {
      this.showSetupError();
      return;
    }

    this.heroHp = this.playerMaxHp;
    this.loadFloor(this.loadFloorNumber());
  }

  // ホーム画面の「1階からはじめる」用：セーブがあっても破棄してから1階スタートする
  async startFromFirstFloor() {
    this.resetProgress();
    await this.startGame();
  }

  // やられた画面の「続きから始める」用：進行状況は保持したまま、同じ階にHP全快で再挑戦する
  continueFromCurrentFloor() {
    this.heroHp = this.playerMaxHp;
    // HPと同様、この階も出直し扱いにする（そうしないと前回使った「おたすけ」が
    // 使用済みのまま引き継がれてしまい、押せなくなる）
    delete this.floorCache[this.currentFloor];
    this.saveFloorNumber();
    this.tryPlayBgm();
    this.loadFloor(this.currentFloor);
  }

  // モンスターが1体も準備できていない（画像未設定等）場合のガイド表示
  showSetupError() {
    this.hideCommandMenu();
    this.setCommandLabel('');
    this.setStatus('モンスターの準備中です。管理画面でdungeon_monsterに画像を設定してください。');
  }

  // ---- 取得・永続化 ----
  async fetchMonsters() {
    if (!window.RPGQuiz) {
      console.error('RPGQuiz（rpg-quiz-loader.js）が読み込まれていません');
      return [];
    }
    try {
      return await window.RPGQuiz.fetchDungeonMonsters();
    } catch (err) {
      console.error('モンスターデータの取得に失敗しました', err);
      return [];
    }
  }

  parseBackgrounds(root) {
    const scriptEl = root.querySelector('[data-role="rpg-dungeon-backgrounds"]');
    try {
      const data = JSON.parse(scriptEl?.textContent || '{}');
      return {
        dungeon: Array.isArray(data.dungeon) ? data.dungeon : [],
        boss: data.boss || '',
        goal: data.goal || '',
      };
    } catch (err) {
      console.error('背景画像データの読み込みに失敗しました', err);
      return { dungeon: [], boss: '', goal: '' };
    }
  }

  parseHelperIcons(root) {
    const scriptEl = root.querySelector('[data-role="rpg-dungeon-helper-icons"]');
    try {
      return JSON.parse(scriptEl?.textContent || '{}');
    } catch (err) {
      console.error('助っ人アイコンデータの読み込みに失敗しました', err);
      return {};
    }
  }

  loadFloorNumber() {
    try {
      const raw = localStorage.getItem(this.storageKey);
      const floor = parseInt(raw, 10);
      if (Number.isInteger(floor) && floor >= 1 && floor <= this.totalFloors) return floor;
    } catch (err) {
      console.error('進行状況の読み込みに失敗しました', err);
    }
    return 1;
  }

  saveFloorNumber() {
    try {
      localStorage.setItem(this.storageKey, String(this.currentFloor));
    } catch (err) {
      console.error('進行状況の保存に失敗しました', err);
    }
  }

  // クリア済みの進行状況は「続きから」の対象にしない。次回訪問時は「ゲームスタート」から仕切り直す
  clearSavedProgress() {
    try {
      localStorage.removeItem(this.storageKey);
    } catch (err) {
      console.error('進行状況のクリアに失敗しました', err);
    }
  }

  setBackground(url) {
    if (!url) return;
    this.root.style.backgroundImage = `linear-gradient(rgba(10, 8, 6, 0.55), rgba(10, 8, 6, 0.55)), url("${url}")`;
  }

  // 同じ階に再訪（にげる・やられた後の再挑戦）しても敵と背景が変わらないよう、階ごとに固定する。
  // 通常モンスターはprepareData()でシャッフル済みのmonsterPoolから1階につき1体ずつ重複なしで割り当て、
  // 最終階（bossFloor）にだけBOSSを配置する
  getFloorAssignment(floorNumber) {
    if (this.floorCache[floorNumber]) return this.floorCache[floorNumber];

    const isBoss = floorNumber === this.bossFloor;
    const monster = isBoss ? this.bossMonster : this.monsterPool[floorNumber - 1];
    const background = isBoss
      ? this.backgrounds.boss
      : this.backgrounds.dungeon[Math.floor(Math.random() * this.backgrounds.dungeon.length)];

    const assignment = {
      isBoss,
      monster,
      background,
      // モンスターに紐づく出題リスト（正解のたびに次へ進む。全問正解で撃退）
      questions: monster ? monster.questions : [],
      questionIndex: 0,
      removedChoiceIndex: null,
      helpUsed: false,
    };
    this.floorCache[floorNumber] = assignment;
    return assignment;
  }

  // ---- 表示 ----
  setStatus(message) {
    if (this.statusEl) this.statusEl.textContent = message || '';
  }

  // コマンド一覧と同じ額縁の中に表示するラベル（「〇〇が現れた！」「コマンドをえらんでね！」など）
  setCommandLabel(message) {
    if (this.commandLabelEl) this.commandLabelEl.textContent = message || '';
  }

  updateHearts() {
    if (!this.heartsEl) return;
    const filled = Math.max(0, Math.min(this.heroHp, this.playerMaxHp));
    const empty = this.playerMaxHp - filled;
    this.heartsEl.textContent = '♥'.repeat(filled) + '♡'.repeat(empty);
  }

  // ゲーム風の「テロップ＋▶」を表示し、クリックされるまで次の処理へ進まないようにする
  // （質問と選択肢は隠し、結果メッセージだけをテロップとして見せる）。
  // iconUrlを渡すと、発言者の顔アイコンをテキストの左に添える（例：おたすけの助っ人）
  showTelop(message, variant, onContinue, { shake = false, iconUrl = '' } = {}) {
    if (this.quizPanelEl) this.quizPanelEl.style.display = 'none';
    this.questionEl.innerHTML = '';
    this.answersEl.innerHTML = '';

    const telop = document.createElement('div');
    telop.className = `rpg-dungeon-telop rpg-dungeon-telop--${variant}`;
    if (shake) telop.classList.add('is-hit');
    const iconHtml = iconUrl ? `<img class="rpg-dungeon-telop__icon" src="${iconUrl}" alt="">` : '';
    telop.innerHTML = `<span class="rpg-dungeon-telop__content">${iconHtml}<span class="rpg-dungeon-telop__text">${message}</span></span><span class="rpg-dungeon-telop__arrow">▶</span>`;
    telop.addEventListener(
      'click',
      () => {
        telop.remove();
        onContinue();
      },
      { once: true }
    );

    this.feedbackEl.innerHTML = '';
    this.feedbackEl.appendChild(telop);
  }

  loadFloor(floorNumber) {
    this.clearTimer();
    this.currentFloor = floorNumber;
    const { isBoss, monster, background } = this.getFloorAssignment(floorNumber);
    this.monster = monster;
    if (!this.monster) return;

    // 勇者のHPは階をまたいで引き継ぐ（全回復するのは1階からのやり直し時のみ）
    // 全問正解で撃退なので、紐づく出題数がそのままモンスターのHPになる
    this.monsterHp = this.monster.questions.length;

    this.root.classList.remove('is-blackout');
    this.setBackground(background);
    this.floorLabelEl.textContent = isBoss ? `${floorNumber}F BOSS` : `${floorNumber}F`;
    this.floorLabelEl.classList.toggle('is-boss', isBoss);
    this.monsterNameEl.textContent = this.monster.monsterName;
    this.stageEl.classList.remove('is-defeated');
    this.stageEl.innerHTML = '';
    const monsterImg = document.createElement('img');
    monsterImg.className = 'rpg-dungeon-stage__image';
    monsterImg.src = this.monster.image;
    monsterImg.alt = this.monster.monsterName;
    this.stageEl.appendChild(monsterImg);
    this.monsterImgEl = monsterImg;
    if (this.quizPanelEl) this.quizPanelEl.style.display = 'none';
    this.questionEl.innerHTML = '';
    this.answersEl.innerHTML = '';
    this.feedbackEl.innerHTML = '';
    this.setStatus('');
    this.setCommandLabel(`${this.monster.monsterName}が現れた！`);
    this.updateHearts();
    this.showCommandMenu();
  }

  // ---- コマンド ----
  bindCommands() {
    this.commandsEl.querySelectorAll('[data-command]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const command = btn.dataset.command;
        if (command === 'fight') this.startTurn({ withHint: false });
        else if (command === 'help') this.startTurn({ withHint: true });
        else if (command === 'flee') this.flee();
      });
    });
  }

  // 矢印キーでの選択移動とEnter/Spaceでの決定を、状況（コマンド/回答選択肢/テロップ）に応じて
  // まとめて扱う。テロップ表示中はEnter/Spaceがクリックと同じ扱いになる。
  bindKeyboardNav() {
    document.addEventListener('keydown', (event) => {
      const telop = this.feedbackEl.querySelector('.rpg-dungeon-telop');
      if (telop) {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          telop.click();
        }
        return;
      }

      if (this.commandsEl.style.display !== 'none') {
        this.handleArrowSelect(event, this.commandsEl.querySelectorAll('[data-command]'));
        return;
      }

      if (this.answersEl.children.length > 0) {
        this.handleArrowSelect(event, this.answersEl.querySelectorAll('button'));
        return;
      }

      // 「次の階へ」「1階からやり直す」「クリア後リンク」などをEnter/Spaceで押せるようにする
      const feedbackAction = this.feedbackEl.querySelector('.start-btn');
      if (feedbackAction && (event.key === 'Enter' || event.key === ' ')) {
        event.preventDefault();
        feedbackAction.click();
      }
    });
  }

  handleArrowSelect(event, allButtons) {
    const buttons = Array.from(allButtons).filter((btn) => !btn.disabled);
    if (buttons.length === 0) return;

    let index = buttons.findIndex((btn) => btn.classList.contains('is-selected'));
    if (index === -1) index = 0;

    if (event.key === 'ArrowRight' || event.key === 'ArrowDown') {
      index = (index + 1) % buttons.length;
    } else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') {
      index = (index - 1 + buttons.length) % buttons.length;
    } else if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      buttons[index].click();
      return;
    } else {
      return;
    }

    event.preventDefault();
    buttons.forEach((btn) => btn.classList.remove('is-selected'));
    buttons[index].classList.add('is-selected');
  }

  showCommandMenu() {
    this.commandsEl.style.display = '';
    if (this.quizPanelEl) this.quizPanelEl.style.display = 'none';
    this.questionEl.innerHTML = '';
    this.answersEl.innerHTML = '';
    const buttons = Array.from(this.commandsEl.querySelectorAll('[data-command]'));
    const helpBtn = this.commandsEl.querySelector('[data-command="help"]');
    if (helpBtn) helpBtn.disabled = this.floorCache[this.currentFloor]?.helpUsed === true;

    buttons.forEach((btn) => btn.classList.remove('is-selected'));
    const firstEnabled = buttons.find((btn) => !btn.disabled);
    if (firstEnabled) firstEnabled.classList.add('is-selected');
  }

  hideCommandMenu() {
    this.commandsEl.style.display = 'none';
  }

  shuffle(array) {
    return [...array].sort(() => Math.random() - 0.5);
  }

  // ---- 出題 ----
  // モンスターに紐づく出題リストは init() 時点で取得済みなので、ターン開始時は同期的に参照するだけでよい
  startTurn({ withHint }) {
    this.clearTimer();
    this.hideCommandMenu();
    if (this.quizPanelEl) this.quizPanelEl.style.display = 'block';
    this.feedbackEl.innerHTML = '';
    this.setStatus('');
    this.answersEl.innerHTML = '';

    const assignment = this.floorCache[this.currentFloor];
    const data = assignment.questions.length
      ? assignment.questions[assignment.questionIndex % assignment.questions.length]
      : null;
    if (!data) {
      this.questionEl.textContent = 'このモンスターに出題できるクイズがまだありません。';
      window.setTimeout(() => this.showCommandMenu(), 1500);
      return;
    }

    // おたすけは1階（1体のモンスター）につき1回だけ使える
    const isFirstHint = withHint && !assignment.helpUsed;
    if (isFirstHint) {
      this.showHelperSelect(data, assignment);
      return;
    }

    this.renderQuestion(data);
  }

  // おたすけ：誰に助けてもらうか選ぶ画面。今はさかもっちゃんしかいないが、
  // this.helpers に追加するだけで選択肢が増える設計にしてある
  showHelperSelect(data, assignment) {
    if (this.timerEl) this.timerEl.style.display = 'none';
    this.questionEl.textContent = '誰に助けてもらう？';
    this.answersEl.innerHTML = '';

    this.helpers.forEach((helper) => {
      const button = document.createElement('button');
      button.className = 'quiz-answer-btn';
      const iconHtml = helper.icon
        ? `<img class="quiz-answer-btn__icon" src="${helper.icon}" alt="">`
        : '';
      button.innerHTML = `<span class="quiz-answer-btn__arrow">▶</span>${iconHtml}${helper.name}`;
      button.addEventListener('click', () => this.useHelper(helper, data, assignment));
      this.answersEl.appendChild(button);
    });

    const firstBtn = this.answersEl.querySelector('button');
    if (firstBtn) firstBtn.classList.add('is-selected');
  }

  // 選んだ助っ人の効果を発動する。効果の中身は helper.applyEffect が担当するので、
  // メンバーごとに異なる効果を持たせられる
  useHelper(helper, data, assignment) {
    assignment.helpUsed = true;
    assignment.helperId = helper.id;
    helper.applyEffect(data, assignment);

    if (this.quizPanelEl) this.quizPanelEl.style.display = 'none';
    this.playHelpSfx();
    this.showTelop(helper.helpMessage, 'help', () => this.renderQuestion(data), { iconUrl: helper.icon });
  }

  renderQuestion(data) {
    const assignment = this.floorCache[this.currentFloor];
    if (this.quizPanelEl) this.quizPanelEl.style.display = 'block';
    if (this.timerEl) this.timerEl.style.display = '';
    this.feedbackEl.innerHTML = '';
    this.questionEl.innerHTML = data.question;
    this.answersEl.innerHTML = '';

    let choices = data.answers.map((answer, index) => ({ answer, index }));
    if (assignment.removedChoiceIndex !== null) {
      choices = choices.filter((c) => c.index !== assignment.removedChoiceIndex);
    }

    choices = this.shuffle(choices);

    choices.forEach(({ answer, index: originalIndex }) => {
      const button = document.createElement('button');
      button.className = 'quiz-answer-btn';
      button.innerHTML = `<span class="quiz-answer-btn__arrow">▶</span>${answer}`;
      button.addEventListener('click', () => this.handleAnswer(originalIndex, data));
      this.answersEl.appendChild(button);
    });

    const firstAnswerBtn = this.answersEl.querySelector('button');
    if (firstAnswerBtn) firstAnswerBtn.classList.add('is-selected');

    this.startTimer();
  }

  // 制限時間バーを表示し、time_limit_seconds かけて満タンから空になるアニメーションを走らせる。
  // 時間切れになったら回答なしのまま敵のターンへ進む（出題自体は同じ問題のまま据え置き）
  startTimer() {
    this.clearTimer();
    this.questionActive = true;
    if (this.timerBarEl) {
      this.timerBarEl.style.animationDuration = `${this.timeLimitSeconds}s`;
      this.timerBarEl.classList.remove('is-running');
      void this.timerBarEl.offsetWidth;
      this.timerBarEl.classList.add('is-running');
    }
    this.timerTimeoutId = window.setTimeout(() => this.handleTimeout(), this.timeLimitSeconds * 1000);
  }

  clearTimer() {
    if (this.timerTimeoutId) {
      window.clearTimeout(this.timerTimeoutId);
      this.timerTimeoutId = null;
    }
    this.questionActive = false;
    if (this.timerBarEl) {
      this.timerBarEl.classList.remove('is-running');
      this.timerBarEl.style.width = '100%';
      this.timerBarEl.style.backgroundColor = '#6fd66f';
    }
  }

  handleTimeout() {
    if (!this.questionActive) return;
    this.questionActive = false;
    this.answersEl.querySelectorAll('button').forEach((btn) => {
      btn.disabled = true;
    });
    const assignment = this.floorCache[this.currentFloor];
    const data = assignment?.questions?.length
      ? assignment.questions[assignment.questionIndex % assignment.questions.length]
      : null;
    this.recordWrongQuestion(data);
    this.monsterTurn();
  }

  // 間違えた（時間切れ含む）問題を、クリア画面から見返せるように記録する（同じ問題は重複させない）
  recordWrongQuestion(data) {
    if (!data) return;
    const alreadyRecorded = this.wrongQuestions.some((entry) => entry.question === data.question);
    if (alreadyRecorded) return;
    this.wrongQuestions.push({
      monsterName: this.monster.monsterName,
      question: data.question,
      correctAnswer: data.answers[data.correct],
      explanation: data.explanation,
      explanationImage: data.explanationImage,
    });
  }

  // 顧客メタフィールド由来のheroNameをinnerHTMLへ差し込む前にエスケープする
  escapeHtml(text) {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }

  // テロップ本文を組み立てる。1行目だけ正解/不正解カラーを付け、以降は全テロップ共通の基本文字色にする
  buildTelopMessage(variant, lines) {
    const [first, ...rest] = lines;
    const restHtml = rest.map((line) => `<br>${line}`).join('');
    return `<span class="rpg-dungeon-telop__highlight rpg-dungeon-telop__highlight--${variant}">${first}</span>${restHtml}`;
  }

  handleAnswer(selected, data) {
    if (!this.questionActive) return;
    this.clearTimer();
    const assignment = this.floorCache[this.currentFloor];
    if (selected === data.correct) {
      // 正解時はモンスターの反撃を受けない。次のターンは次の問題に進む
      this.monsterHp = Math.max(0, this.monsterHp - 1);
      assignment.questionIndex++;
      assignment.removedChoiceIndex = null;
      this.playAttackSfx();
      this.playHitAnimation();
      const message = this.buildTelopMessage('correct', [
        '✅ 正解！',
        `${this.heroName}のこうげき！`,
        `${this.monster.monsterName}は1のダメージを受けた！`,
      ]);

      if (this.monsterHp <= 0) {
        this.showTelop(message, 'correct', () => this.handleVictory());
        return;
      }

      this.showTelop(message, 'correct', () => {
        this.setCommandLabel('コマンドをえらんでね！');
        this.showCommandMenu();
      });
    } else {
      // 不正解時は攻撃が外れるだけでなく、続けてモンスターのターンが発生する
      this.recordWrongQuestion(data);
      const message = this.buildTelopMessage('wrong', ['❌ 不正解！', `${this.heroName}のこうげきは外れた！`]);
      this.showTelop(message, 'wrong', () => this.monsterTurn());
    }
  }

  monsterTurn() {
    const damage = this.monster.isBoss ? 2 : 1;
    this.heroHp = Math.max(0, this.heroHp - damage);
    this.updateHearts();
    this.playAttackSfx();
    const message = `${this.monster.monsterName}のこうげき！<br>${this.heroName}は${damage}のダメージを受けた！`;

    if (this.heroHp <= 0) {
      this.showTelop(message, 'wrong', () => this.handleDefeat(), { shake: true });
      return;
    }

    this.showTelop(
      message,
      'wrong',
      () => {
        this.setCommandLabel('コマンドをえらんでね！');
        this.showCommandMenu();
      },
      { shake: true }
    );
  }

  // ---- ボタン生成ヘルパー ----
  // フィードバック欄に並ぶ「start-btn」見た目のボタン／リンクは、
  // クリア画面・やられた画面の両方でほぼ同じ形が繰り返し出てくるため共通化しておく。
  createActionButton(text, onClick, extraClassName = '') {
    const button = document.createElement('button');
    button.type = 'button';
    button.className = extraClassName ? `start-btn ${extraClassName}` : 'start-btn';
    button.textContent = text;
    button.addEventListener('click', onClick);
    return button;
  }

  createActionLink(text, href, extraClassName = '') {
    const link = document.createElement('a');
    link.className = extraClassName ? `start-btn ${extraClassName}` : 'start-btn';
    link.href = href;
    link.textContent = text;
    return link;
  }

  // 「間違えた問題の解説を見る」ボタン（クリア画面・やられた画面で共通）
  createReviewButton() {
    return this.createActionButton('間違えた問題の解説を見る', () => this.openReviewModal(), 'rpg-dungeon-share-btn');
  }

  // 「Xでシェア！」ボタン（クリア画面・やられた画面で共通。文言だけ差し替え可能。
  // text未指定時はshareOnSNS()側のデフォルト文言が使われる）
  createShareButton(text) {
    const button = this.createActionButton('', () => this.shareOnSNS(text), 'rpg-dungeon-share-btn rpg-dungeon-x-share-btn');
    button.innerHTML =
      '<svg class="rpg-dungeon-share-btn__icon" viewBox="0 0 24 24" width="18" height="18" fill="currentColor" aria-hidden="true"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>でシェア！';
    return button;
  }

  // ---- 勝敗 ----
  handleVictory() {
    this.stageEl.classList.add('is-defeated');
    if (this.quizPanelEl) this.quizPanelEl.style.display = 'none';
    this.questionEl.innerHTML = '';
    this.answersEl.innerHTML = '';
    this.hideCommandMenu();
    this.playVictorySfx();

    const message = this.buildTelopMessage('correct', [`${this.monster.monsterName}を倒した！`]);
    this.showTelop(message, 'correct', () => this.showVictoryContinue());
  }

  // 「〇〇を倒した！」テロップを閉じた後、次の階へ／クリアの続きを表示する
  showVictoryContinue() {
    this.feedbackEl.innerHTML = '';

    const isLastFloor = this.currentFloor >= this.totalFloors;

    if (isLastFloor) {
      this.setBackground(this.backgrounds.goal);
      if (this.bgmEl) this.bgmEl.pause();
      this.playClearSfx();
      this.clearSavedProgress();
      this.monsterNameEl.classList.add('is-cleared');
      this.stageEl.classList.add('is-cleared');

      const heading = document.createElement('div');
      heading.className = 'rpg-dungeon-clear-heading';
      heading.textContent = '🎉 DUNGEON CLEAR 🎉';
      this.feedbackEl.appendChild(heading);

      const message = document.createElement('p');
      message.className = 'rpg-dungeon-clear-message';
      message.innerHTML = this.clearMessage;
      this.feedbackEl.appendChild(message);

      if (this.clearButtonLabel && this.clearButtonLink) {
        this.feedbackEl.appendChild(this.createActionLink(this.clearButtonLabel, this.clearButtonLink));
      }

      if (this.wrongQuestions.length > 0) {
        this.feedbackEl.appendChild(this.createReviewButton());
      }

      this.feedbackEl.appendChild(this.createShareButton('ショピダンジョンをクリアしたよ！🎉'));
      return;
    }

    const nextBtn = this.createActionButton('次の階へ ➡', () => {
      this.playStairsSfx();
      this.currentFloor++;
      this.saveFloorNumber();
      this.loadFloor(this.currentFloor);
    });
    this.feedbackEl.appendChild(nextBtn);
  }

  // やられた時・にげた時は、進行状況を1Fからやり直しにする
  // （階のモンスター/背景/出題の割り当てもリセットし、次に1Fへ来た時は改めて抽選し直す）
  resetProgress() {
    this.floorCache = {};
    this.currentFloor = 1;
    this.heroHp = this.playerMaxHp;
    this.wrongQuestions = [];
    this.saveFloorNumber();
  }

  restartFromFirstFloor() {
    this.resetProgress();
    this.playStairsSfx();
    this.tryPlayBgm();
    this.loadFloor(1);
  }

  // にげた時はゲームをホーム画面まで戻す。再開時は「ゲームスタート」から仕切り直す
  returnToStartScreen() {
    this.resetProgress();
    this.root.classList.remove('is-blackout');
    this.root.classList.remove('is-started');
    if (this.startBtnEl) this.startBtnEl.textContent = 'ゲームスタート';
    if (this.continueBtnEl) this.continueBtnEl.hidden = true;
    if (this.bgmEl) this.bgmEl.pause();
  }

  handleDefeat() {
    this.setStatus('やられた…！一度出直そう。');
    if (this.bgmEl) this.bgmEl.pause();
    if (this.quizPanelEl) this.quizPanelEl.style.display = 'none';
    this.feedbackEl.innerHTML = '';
    this.questionEl.innerHTML = '';
    this.answersEl.innerHTML = '';
    this.hideCommandMenu();

    if (this.wrongQuestions.length > 0) {
      this.feedbackEl.appendChild(this.createReviewButton());
    }

    const btnRow = document.createElement('div');
    btnRow.className = 'rpg-dungeon-btn-row';
    btnRow.appendChild(this.createActionButton('続きから始める', () => this.continueFromCurrentFloor()));
    btnRow.appendChild(this.createActionButton('1階からやり直す', () => this.restartFromFirstFloor()));
    this.feedbackEl.appendChild(btnRow);

    this.feedbackEl.appendChild(this.createShareButton());
  }

  // やられた階／クリアをXでシェアする。
  // navigator.share（OSの共有シート）経由だとX側の受け取りで日本語が文字化けする不具合があるため使わず、
  // 常にXの投稿画面（intent）を直接開く（encodeURIComponentなら文字化けしないことを確認済み）
  shareOnSNS(text = `ショピダンジョンで${this.currentFloor}階まで到達したよ！`) {
    const url = window.location.href;

    const shareUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(url)}`;
    window.open(shareUrl, '_blank', 'noopener');
  }

  flee() {
    this.hideCommandMenu();
    if (this.quizPanelEl) this.quizPanelEl.style.display = 'none';
    this.setStatus('');
    this.questionEl.innerHTML = '';
    this.answersEl.innerHTML = '';
    this.root.classList.add('is-blackout');
    this.playStairsSfx();

    const message = `${this.monster.monsterName}の前から逃げ出した…<br>1階から出直そう。`;
    this.showTelop(message, 'wrong', () => this.returnToStartScreen());
  }
}

document.querySelectorAll('.rpg-dungeon-section').forEach((root) => {
  // theme dev のホットリロード等でこのスクリプトが同じDOMに対して再実行された場合の二重初期化を防ぐ
  if (root.dataset.rpgDungeonInitialized === 'true') return;
  root.dataset.rpgDungeonInitialized = 'true';
  new RPGDungeon(root);
});
